import { Box, Button, Divider, Paper, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import DrawerListener from '@components/Drawer';
import Icon from '@components/Icon/Icon';
import RoundedButton from '@components/RoundedButton';
import { StatusPill } from '@components/StatusPill';

import {
  APP_VERSION_APP_LABEL_MESSAGES,
  APP_VERSION_PLATFORM_ICONS,
  APP_VERSION_PLATFORM_LABEL_MESSAGES,
} from '@constants/app-version';

import { formatDate } from '@utils/format';

import type { AppVersionSummaryFieldsFragment } from 'generated/app-version.generated';

import ReleaseForm, { type ReleaseFormMode } from './ReleaseForm';
import ReleaseHistory from './ReleaseHistory';

const messages = defineMessages({
  published: {
    id: 'admin.configurations.published',
    defaultMessage: 'Published',
  },
  draft: {
    id: 'admin.configurations.draft',
    defaultMessage: 'Draft',
  },
  latestVersion: {
    id: 'admin.configurations.latestVersion',
    defaultMessage: 'Latest version',
  },
  publishedOn: {
    id: 'admin.configurations.publishedOn',
    defaultMessage: 'Published {date}',
  },
  minimumVersion: {
    id: 'admin.configurations.minimumVersionLabel',
    defaultMessage: 'Minimum version',
  },
  minBadge: {
    id: 'admin.configurations.minBadge',
    defaultMessage: 'Min',
  },
  noPublished: {
    id: 'admin.configurations.noPublished',
    defaultMessage: 'No published release',
  },
  draftSummary: {
    id: 'admin.configurations.draftSummary',
    defaultMessage: 'Draft - v{version}',
  },
  noDraftNotes: {
    id: 'admin.configurations.noDraftNotes',
    defaultMessage: 'No release notes yet',
  },
  createRelease: {
    id: 'admin.configurations.createRelease',
    defaultMessage: 'Create new release',
  },
  editDraft: {
    id: 'admin.configurations.editDraft',
    defaultMessage: 'Edit draft',
  },
  editingBelow: {
    id: 'admin.configurations.editingBelow',
    defaultMessage: 'Editing below ↓',
  },
  history: {
    id: 'admin.configurations.history',
    defaultMessage: 'History',
  },
  historyTitle: {
    id: 'admin.configurations.historyTitle',
    defaultMessage: 'Release History - {app} {platform}',
  },
});

interface PlatformCardProps {
  summary: AppVersionSummaryFieldsFragment;
}

function PlatformCard({ summary }: PlatformCardProps) {
  const { formatMessage } = useIntl();
  const { palette } = useTheme();
  const [formMode, setFormMode] = useState<ReleaseFormMode | null>(null);

  const appLabel = formatMessage(APP_VERSION_APP_LABEL_MESSAGES[summary.app]);
  const platformLabel = formatMessage(
    APP_VERSION_PLATFORM_LABEL_MESSAGES[summary.platform]
  );
  const platformIcon = APP_VERSION_PLATFORM_ICONS[summary.platform];

  const openCreate = () => {
    setFormMode('create');
  };

  const openEdit = () => {
    setFormMode('edit');
  };

  const closeForm = () => {
    setFormMode(null);
  };

  const historyTitle = formatMessage(messages.historyTitle, {
    app: appLabel,
    platform: platformLabel,
  });

  const isFormOpen = Boolean(formMode);
  const showMinColumn =
    Boolean(summary.minimum_version) && !summary.latest_is_minimum_version;
  const showMinBadge = Boolean(summary.latest_is_minimum_version);

  const statusPill = summary.has_draft ? (
    <StatusPill
      color={palette.warning.main}
      name={formatMessage(messages.draft)}
      variant="primary"
    />
  ) : summary.latest_published_version ? (
    <StatusPill
      color={palette.success.main}
      name={formatMessage(messages.published)}
      variant="primary"
    />
  ) : null;

  return (
    <Stack
      data-testid={`platform-card-${summary.app}-${summary.platform}`}
      spacing={1.5}
    >
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${palette.divider}`,
          borderRadius: 2,
          overflow: 'hidden',
        }}
        variant="outlined"
      >
        <Stack
          alignItems="center"
          direction="row"
          flexWrap="wrap"
          gap={1}
          justifyContent="space-between"
          px={2}
          py={1.25}
          sx={{ borderBottom: `1px solid ${palette.divider}` }}
        >
          <Stack alignItems="center" direction="row" flexWrap="wrap" gap={1}>
            <Icon name={platformIcon} type="fab" />
            <Typography
              component="h3"
              fontWeight={700}
              textTransform="uppercase"
              variant="subtitle1"
            >
              {platformLabel}
            </Typography>
            {statusPill}
          </Stack>

          <DrawerListener
            content={<ReleaseHistory summary={summary} />}
            title={historyTitle}
          >
            {({ openDrawer }) => (
              <Button
                color="inherit"
                startIcon={
                  <Icon
                    color={palette.text.secondary}
                    name="clock-rotate-left"
                    type="fas"
                  />
                }
                sx={{ color: palette.text.secondary, textTransform: 'none' }}
                onClick={openDrawer}
              >
                {formatMessage(messages.history)}
              </Button>
            )}
          </DrawerListener>
        </Stack>

        <Stack
          alignItems={{ xs: 'stretch', sm: 'center' }}
          direction={{ xs: 'column', sm: 'row' }}
          divider={
            <Divider
              flexItem
              orientation="vertical"
              sx={{ display: { xs: 'none', sm: 'block' } }}
            />
          }
          px={2}
          py={2}
          spacing={2}
        >
          <Stack direction="row" flex={1} flexWrap="wrap" gap={3} minWidth={0}>
            {showMinColumn ? (
              <Box>
                <Typography
                  color="text.secondary"
                  textTransform="uppercase"
                  variant="caption"
                >
                  {formatMessage(messages.minimumVersion)}
                </Typography>
                <Typography
                  color="primary"
                  data-testid="minimum-version"
                  fontWeight={700}
                  variant="h5"
                >
                  {summary.minimum_version}
                </Typography>
              </Box>
            ) : null}

            <Box>
              <Typography
                color="text.secondary"
                textTransform="uppercase"
                variant="caption"
              >
                {formatMessage(messages.latestVersion)}
              </Typography>
              <Stack
                alignItems="center"
                direction="row"
                flexWrap="wrap"
                gap={1}
              >
                <Typography
                  color={
                    summary.latest_published_version
                      ? 'primary'
                      : 'text.secondary'
                  }
                  data-testid="latest-version"
                  fontWeight={700}
                  variant="h5"
                >
                  {summary.latest_published_version ??
                    formatMessage(messages.noPublished)}
                </Typography>
                {showMinBadge ? (
                  <StatusPill
                    color={palette.success.main}
                    name={formatMessage(messages.minBadge)}
                    variant="primary"
                  />
                ) : null}
              </Stack>
              {summary.latest_published_at ? (
                <Typography
                  color="text.secondary"
                  data-testid="latest-published-at"
                  variant="body2"
                >
                  {formatMessage(messages.publishedOn, {
                    date: formatDate({
                      date: summary.latest_published_at,
                      formatter: 'date',
                    }),
                  })}
                </Typography>
              ) : null}
            </Box>
          </Stack>

          <Stack
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="center"
            minWidth={{ sm: 200 }}
            pl={{ sm: 1 }}
          >
            {isFormOpen ? (
              <Typography color="primary" fontWeight={600} variant="body2">
                {formatMessage(messages.editingBelow)}
              </Typography>
            ) : summary.has_draft ? (
              <Button
                color="primary"
                startIcon={<Icon name="pen" type="fas" />}
                sx={{ borderRadius: '29px', textTransform: 'none' }}
                variant="outlined"
                onClick={openEdit}
              >
                {formatMessage(messages.editDraft)}
              </Button>
            ) : (
              <RoundedButton
                autoSize
                iconName="plus"
                margin={0}
                textTransform="none"
                variant="blue"
                onClick={openCreate}
              >
                {formatMessage(messages.createRelease)}
              </RoundedButton>
            )}
          </Stack>
        </Stack>

        {summary.has_draft && !isFormOpen ? (
          <Box
            data-testid="draft-preview"
            mx={2}
            mb={2}
            sx={{
              border: `1px dashed ${palette.divider}`,
              borderRadius: 1,
              p: 1.5,
            }}
          >
            <Typography fontWeight={700} variant="body2">
              {formatMessage(messages.draftSummary, {
                version: summary.draft_version ?? '?',
              })}
            </Typography>
            <Typography
              color="text.secondary"
              fontStyle="italic"
              mt={0.5}
              variant="body2"
            >
              {summary.draft_release_notes?.trim() ||
                formatMessage(messages.noDraftNotes)}
            </Typography>
          </Box>
        ) : null}
      </Paper>

      {formMode ? (
        <ReleaseForm mode={formMode} summary={summary} onClose={closeForm} />
      ) : null}
    </Stack>
  );
}

export default PlatformCard;
